import { IsomProject, PlacedBlock, SocketConnection } from '@/app/types/cad';
import { TableConfig } from '@/app/types/table';
import { PrintConfig, PrintPreset } from '@/app/types/print';
import { toast } from 'sonner';
import { 
  loadPrintConfig, 
  savePrintConfig, 
  loadAllPresets, 
  saveCustomPresets 
} from './printPersistence';

/**
 * Universal file saving helper that uses the File System Access API if available.
 * Allows users to choose directory and filename ("Save As" behavior).
 * Falls back to standard browser download.
 */
export async function saveFileWithDialog(
  blob: Blob,
  filename: string,
  description: string,
  extension: string,
  mimeType: string
): Promise<string | null> {
  try {
    // Check if File System Access API is supported (Chrome, Edge, Opera)
    // @ts-ignore - window.showSaveFilePicker is not yet in standard TS lib
    if (typeof window.showSaveFilePicker === 'function') {
      const opts = {
        suggestedName: filename,
        types: [
          {
            description,
            accept: { [mimeType]: [`.${extension.replace(/^\./, '')}`] },
          },
        ],
      };
      
      // @ts-ignore
      const handle = await window.showSaveFilePicker(opts);
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return handle.name;
    }
  } catch (error: any) {
    // If user cancelled, just return null
    if (error.name === 'AbortError') return null;
    
    // Log other errors and fall back to download
    console.warn('File System Access API failed, falling back to download:', error);
  }

  // Fallback: Standard Download
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a); // Append to body to ensure click works in FF
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  
  return filename;
}

/**
 * Save project to file including ALL configurations:
 * - Blocks, connections, systems
 * - Table configuration
 * - Print configuration and presets
 */
export async function saveProject(
  projectName: string,
  blocks: PlacedBlock[],
  connections: SocketConnection[],
  tableConfig?: TableConfig
): Promise<void> {
  const now = new Date().toISOString();

  // Load current print settings from localStorage
  const currentPrintConfig = loadPrintConfig();
  const allPresets = loadAllPresets();
  // Only save custom presets, not built-in ones
  const customPresets = allPresets.filter(p => !p.isBuiltIn);

  const project: IsomProject = {
    name: projectName,
    version: '2.1',
    created: now,
    modified: now,
    blocks,
    connections,
    systems: [],
    tableConfig, // Save table config
    // NEW: Save print configurations
    lastPrintConfig: currentPrintConfig || undefined,
    printPresets: customPresets.length > 0 ? customPresets : undefined,
  };

  const blob = new Blob([JSON.stringify(project, null, 2)], {
    type: 'application/json',
  });
  
  const filename = `${projectName.replace(/\s+/g, '_')}.isomproject`;
  
  await saveFileWithDialog(
    blob,
    filename,
    'IsoBlock Project',
    'isomproject',
    'application/json'
  );

  toast.success('Projekt gespeichert (inkl. Druckeinstellungen)');
}

/**
 * Load project from file and restore ALL configurations:
 * - Blocks, connections, systems
 * - Table configuration
 * - Print configuration and presets
 */
export function loadProjectFromFile(
  file: File,
  onSuccess: (project: IsomProject) => void
): void {
  const reader = new FileReader();
  
  reader.onload = (event) => {
    try {
      const project: IsomProject = JSON.parse(event.target?.result as string);
      
      // Restore print configuration to localStorage
      if (project.lastPrintConfig) {
        try {
          savePrintConfig(project.lastPrintConfig);
          console.log('✅ Restored print configuration from project');
        } catch (error) {
          console.error('⚠️ Failed to restore print config:', error);
        }
      }
      
      // Restore custom print presets to localStorage
      if (project.printPresets && project.printPresets.length > 0) {
        try {
          // Convert date strings back to Date objects
          const restoredPresets = project.printPresets.map(preset => ({
            ...preset,
            createdAt: new Date(preset.createdAt),
            updatedAt: preset.updatedAt ? new Date(preset.updatedAt) : undefined,
          }));
          saveCustomPresets(restoredPresets);
          console.log(`✅ Restored ${restoredPresets.length} custom print preset(s)`);
        } catch (error) {
          console.error('⚠️ Failed to restore print presets:', error);
        }
      }
      
      onSuccess(project);
      toast.success('Projekt geladen (inkl. Druckeinstellungen)');
    } catch (error) {
      console.error('Error loading project:', error);
      toast.error('Fehler beim Laden des Projekts');
    }
  };
  
  reader.readAsText(file);
}